import ArrowBackLight from "@assets/navigation/back.svg";
import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { invoiceServices } from "@services/apis/Invoice";
import { GetExchangesData } from "@services/models/InvoiceApprovals/GetExchangesModel";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditExchangeSlider from "./components/EditExchangeSlider";
import ExchangeManagementSlider from "./components/ExchangeManagementSlider";
import ExchangeManagementStausPill from "./components/StatusPillExchangeManagement";
import DeleteExchangeSlider from "./components/DeleteExchangeSlider";

const ExchangeManagement = () => {
  const gridRef = useRef<AgGridReact>(null);

  const navigate = useNavigate();

  const [exchangeData, setExchangeData] = useState<GetExchangesData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [addExchangeSliderOpen, setAddExchangeSliderOpen] =
    useState<boolean>(false);
  const [editExchangeSliderOpen, setEditExchangeSliderOpen] =
    useState<boolean>(false);
  const [deleteExchangeSliderOpen, setDeleteExchangeSliderOpen] =
    useState<boolean>(false);
  const [selectedExchange, setSelectedExchange] =
    useState<GetExchangesData | null>(null);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Exchange ID",
        field: "exchangeId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Exchange",
        field: "exchange",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 220,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Token",
        field: "token",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Network",
        field: "network",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Crypto address",
        field: "address",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 350,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}
              </span>
              <CopyButton
                copyText={params.value}
                copyMessage="Address copied!"
                copyIconSize={10}
                buttonSize="16px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <ExchangeManagementStausPill
              statusText={params.data.isActive ? "ACTIVE" : "DEACTIVATED"}
            />
          );
        },
      },
      {
        headerName: "Actions",
        field: "id",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.6,
        minWidth: 60,
        maxWidth: 190,
        filter: null,
        cellRenderer: (params: any) => {
          return (
            <div className="flex justify-start items-center gap-2">
              <div
                className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-border-button-secondary border border-bg-button-secondary"
                style={{ width: "20px", height: "20px", borderRadius: "4px" }}
                onClick={() => {
                  setSelectedExchange(params.data);
                  setEditExchangeSliderOpen(true);
                }}
              >
                <span className="text-[14px] material-symbols-outlined text-text-base">
                  border_color
                </span>
              </div>
              <div
                className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-bg-status-red border border-bg-status-red"
                style={{ width: "20px", height: "20px", borderRadius: "4px" }}
                onClick={() => {
                  setSelectedExchange(params.data);
                  setDeleteExchangeSliderOpen(true);
                }}
              >
                <span className="text-[14px] material-symbols-outlined text-text-base">
                  delete
                </span>
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchExchangeData = async () => {
    try {
      setLoading(true);
      const response = await invoiceServices.getExchanges();
      setExchangeData(response.data);
    } catch (err: any) {
      setError("Failed to fetch exchange data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExchangeData();
  }, []);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-grow justify-between items-center w-full mr-4">
          <div className="flex gap-2 items-center">
            <img
              src={ArrowBackLight}
              alt="Back"
              className="cursor-pointer"
              onClick={() => navigate("/invoice-approvals")}
            />
            <p className="flex text-text-base text-[14px] font-semibold items-center">
              Exchange Management
            </p>
          </div>
          <LightCustomButton
            text="+ ADD EXCHANGE"
            isActive={true}
            onClick={() => setAddExchangeSliderOpen(true)}
          />
        </div>
      </div>

      <div className="flex flex-col bg-bg-page-body py-[20px] px-[12px] h-full">
        {loading ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={gridRef}
            rowData={exchangeData}
            columnDefs={columnDefs}
            getRowId={(params) => params.data.exchangeId?.toString()}
            error={error}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        )}
      </div>
      <ExchangeManagementSlider
        isOpen={addExchangeSliderOpen}
        onClose={() => setAddExchangeSliderOpen(false)}
        onExchangeAdded={() => fetchExchangeData()}
      />
      <EditExchangeSlider
        isOpen={editExchangeSliderOpen}
        onClose={() => {
          setEditExchangeSliderOpen(false);
          setSelectedExchange(null);
        }}
        onExchangeUpdated={() => fetchExchangeData()}
        exchangeData={selectedExchange}
      />
       <DeleteExchangeSlider
        isOpen={deleteExchangeSliderOpen}
        onClose={() => {
          setDeleteExchangeSliderOpen(false);
          setSelectedExchange(null);
        }}
        onExchangeDeleted={() => fetchExchangeData()}
        exchangeData={selectedExchange}
      />
    </>
  );
};

export default ExchangeManagement;
